import { toast } from 'react-toastify'
import { useKeycloak } from '@react-keycloak/web'
import { useEffect, useRef } from 'react'
import { getLastActivity, startTrackingActivity } from '../utils/activityTracker'
import { config } from '../config'

export const useTokenActivityRefresher = () => {
  const { keycloak } = useKeycloak()
  const lastCheckedRef = useRef(Date.now())
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const keycloakRef = useRef(keycloak)

  /* 
    Store the latest keycloak instance in a ref.
    This ensures our setInterval callback and token expiration handler
    always use the most up-to-date keycloak object,
    even if it changes due to re-authentication or provider updates. 
  */
  useEffect(() => {
    keycloakRef.current = keycloak
  }, [keycloak])

  useEffect(() => {
    startTrackingActivity()

    if (!keycloak?.authenticated) return

    // Save previous handler to restore later
    const prevOnTokenExpired = keycloak.onTokenExpired

    const handleTokenExpired = () => {
      const lastChecked = lastCheckedRef.current
      const lastActivity = getLastActivity()
      const wasActive = lastActivity > lastChecked

      if (wasActive) {
        console.log('Token expired, but user was just active, will try to refresh on next tick.')
        return
      }

      toast.warning('You will be redirected to login page due to inactivity')
      setTimeout(() => {
        keycloak.logout?.({ redirectUri: window.location.origin + '/login' })
      }, 2000)
    }

    keycloak.onTokenExpired = handleTokenExpired

    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const lastChecked = lastCheckedRef.current
      const lastActivity = getLastActivity()
      const wasActive = lastActivity > lastChecked

      lastCheckedRef.current = now

      if (!wasActive) {
        console.log('Skipping token refresh due to inactivity')
        return
      }

      const kc = keycloakRef.current
      if (kc?.authenticated && kc.token && typeof kc.updateToken === 'function') {
        // update token if it will be expired within the next 2x tokenRefreshInterval
        kc.updateToken((2 * Number(config.tokenRefreshInterval)) / 1000)
          .then((refreshed: any) => {
            if (refreshed) {
              console.log('Token refreshed at', new Date().toLocaleTimeString())
            } else {
              console.log('Token still valid, no need to refresh')
            }
          })
          .catch((err: any) => {
            console.error('Failed to refresh token:', err)
            kc.logout?.({ redirectUri: window.location.origin + '/login' })
          })
      }
    }, Number(config.tokenRefreshInterval))

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      keycloak.onTokenExpired = prevOnTokenExpired
    }
  }, [keycloak?.authenticated])
}
